import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { AutoVideo } from './AutoVideo.jsx';
import { getDisplayUnits, fToC, formatVolume } from '../units.js';
import { MEDIA_ACCEPT, uploadResidentMedia } from '../livestock-media.js';
import { localDateIso } from '../local-date.js';
import { catalogFieldLabel, catalogValueStored } from '../shown-units.js';

// Species detail sheet — opens from the browser grid.
//
// Layout follows what a reef hobbyist scans (cf. vendor product pages):
//   - Contained hero image (user's own photo when present, else catalog)
//   - Name + scientific + a prominent category chip (SPS / LPS / …) and a
//     discoverable "Suggest an edit" link
//   - Rules verdict banner (green / yellow / red, with the reasons)
//   - Quick facts strip (care level · type · min tank · temp range)
//   - "Target water" card — the per-species ideal ranges, unit-aware
//   - Light & flow row
//   - Sensei's AI take (slides in below)
//   - Diet / habitat prose
//   - Add-to-tank actions
//
// Storage is canonical; temperature renders in the tank's chosen unit
// via units.js. User photos attach to the keeper's resident for this
// species; they go to the admin catalog-review queue only when the keeper
// ticks "Share" (opt-in, owner 2026-10-01).

const VERDICT_LABEL = {
  good: 'Good fit',
  caution: 'Add with caution',
  no: 'Not recommended',
};

const VERDICT_TONE = {
  good: 'lds-verdict-good',
  caution: 'lds-verdict-caution',
  no: 'lds-verdict-no',
};

const LEVEL_ICON = { no: '✕', caution: '!', info: 'i' };

export function LivestockDetailSheet({ tank, catalogId, onAdded, addedOn, onAddedOnChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  // Advice state is separate so the slow Claude call doesn't block
  // the catalog + rules-verdict render.
  const [advice, setAdvice] = useState(null);
  const [adviceLoading, setAdviceLoading] = useState(false);
  // Photo contribution state. ownedResidentId is the keeper's resident
  // for this species (null when they don't own it yet); userImages is
  // that resident's uploaded photos, shown on the hero.
  const [ownedResidentId, setOwnedResidentId] = useState(null);
  const [userImages, setUserImages] = useState([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoMsg, setPhotoMsg] = useState('');
  const [sharePhoto, setSharePhoto] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const fileRef = useRef(null);
  // The resident the next upload attaches to. Mirrors ownedResidentId
  // but as a ref so the file-picker onChange (which fires seconds after
  // addThenAttach sets state) never reads a stale null.
  const residentRef = useRef(null);

  const displayUnits = getDisplayUnits(tank);

  const loadUserImages = async (residentId) => {
    if (!residentId) {
      setUserImages([]);
      return;
    }
    try {
      const imgs = await api(`/api/tanks/${tank.id}/livestock/${residentId}/images`);
      setUserImages(Array.isArray(imgs) ? imgs : []);
    } catch {
      setUserImages([]);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr('');
    setAdvice(null);
    setAdviceLoading(false);
    setOwnedResidentId(null);
    setUserImages([]);
    setSuggestOpen(false);
    setPhotoMsg('');
    api(`/api/tanks/${tank.id}/livestock-catalog/${catalogId}`)
      .then((d) => {
        if (cancelled) return;
        setData(d);
        residentRef.current = d?.owned_resident_id || null;
        if (d?.owned_resident_id) {
          setOwnedResidentId(d.owned_resident_id);
          loadUserImages(d.owned_resident_id);
        }
        if (d?.advice) {
          setAdvice(d.advice);
        } else if (tank.role === 'viewer') {
          // Asking Sensei is billed to the tank's owner, so a viewer reads
          // only a take that is already cached.
          setAdvice(null);
        } else {
          setAdviceLoading(true);
          api(`/api/tanks/${tank.id}/livestock-catalog/${catalogId}/advice`)
            .then((r) => {
              if (cancelled) return;
              setAdvice(r?.advice || null);
            })
            .catch(() => {})
            .finally(() => {
              if (!cancelled) setAdviceLoading(false);
            });
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setErr(e.message || 'Could not load species details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tank.id, catalogId]);

  const handleAdd = async () => {
    setAdding(true);
    setErr('');
    try {
      await api(`/api/tanks/${tank.id}/livestock`, {
        method: 'POST',
        body: {
          catalog_id: catalogId,
          qty,
          added_at: addedOn || localDateIso(),
        },
      });
      await onAdded();
    } catch (e) {
      setErr(e.message || 'Could not add to tank.');
    } finally {
      setAdding(false);
    }
  };

  // Add this species to the tank WITHOUT closing the sheet, then open the
  // photo picker so the keeper can contribute a picture in one motion.
  const addThenAttach = async () => {
    setPhotoBusy(true);
    setPhotoMsg('');
    try {
      const row = await api(`/api/tanks/${tank.id}/livestock`, {
        method: 'POST',
        body: {
          catalog_id: catalogId,
          qty: 1,
          added_at: addedOn || localDateIso(),
        },
      });
      if (row?.id) {
        residentRef.current = row.id;
        setOwnedResidentId(row.id);
        setPhotoBusy(false);
        // Let the picker open against the freshly-created resident.
        fileRef.current?.click();
      } else {
        setPhotoBusy(false);
        setPhotoMsg('Added to tank. Reopen to add a photo.');
      }
    } catch (e) {
      setPhotoBusy(false);
      setPhotoMsg(e.message || 'Could not add to tank.');
    }
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const residentId = residentRef.current || ownedResidentId;
    if (!file || !residentId) return;
    setPhotoBusy(true);
    setPhotoMsg('');
    try {
      await uploadResidentMedia({
        tankId: tank.id,
        residentId,
        file,
        shareWithCatalog: sharePhoto,
      });
      await loadUserImages(residentId);
      setPhotoMsg(
        sharePhoto ? 'Your photo is in, and shared for catalog review.' : 'Your photo is in.'
      );
    } catch (er) {
      setPhotoMsg(er.message || 'Could not upload that photo.');
    } finally {
      setPhotoBusy(false);
    }
  };

  const cat = data?.catalog;
  const fit = data?.fit;
  const raw = cat?.raw || {};
  const care = cat?.care_params && typeof cat.care_params === 'object' ? cat.care_params : {};
  const verdict = fit?.verdict || 'caution';
  const blockedAdd = verdict === 'no';

  // Temperature helpers honour the tank's per-tank °C/°F preference.
  const tUnit = displayUnits.temp;
  const tConv = (f) => (tUnit === 'C' ? fToC(Number(f)) : Number(f));
  const tSuffix = tUnit === 'C' ? '°C' : '°F';
  const fmtTempRange = (loF, hiF) => {
    if (loF == null && hiF == null) return null;
    if (loF != null && hiF != null)
      return `${tConv(loF).toFixed(tUnit === 'C' ? 1 : 0)}-${tConv(hiF).toFixed(tUnit === 'C' ? 1 : 0)}${tSuffix}`;
    const one = loF != null ? loF : hiF;
    return `${tConv(one).toFixed(tUnit === 'C' ? 1 : 0)}${tSuffix}`;
  };
  const range = (r, unit = '') =>
    Array.isArray(r) && r.length === 2 ? `${r[0]}-${r[1]}${unit}` : null;

  // Admin temp range (temp_min_f/temp_max_f) preferred; fall back to the
  // care_params ideal temp band when the admin range isn't set.
  const tempRangeStr =
    cat && (cat.temp_min_f != null || cat.temp_max_f != null)
      ? fmtTempRange(cat.temp_min_f, cat.temp_max_f)
      : Array.isArray(care.temp_f)
        ? fmtTempRange(care.temp_f[0], care.temp_f[1])
        : null;

  // Does the "Target water" card have anything to show?
  const hasWaterCard =
    range(care.alk_dkh, ' dKH') ||
    range(care.calcium_ppm, ' ppm') ||
    range(care.magnesium_ppm, ' ppm') ||
    range(care.nitrate_ppm, ' ppm') ||
    range(care.phosphate_ppm, ' ppm') ||
    tempRangeStr;
  const hasLightFlow = care.light_pref || care.flow_pref || care.placement || care.sensitivity;

  const heroImgUrl = userImages.length
    ? `/api/tanks/${tank.id}/livestock/${ownedResidentId}/image/${userImages[0].id}`
    : null;

  return (
    <div className="lds-page">
      <div className="lds-sheet">
        {loading && (
          <div className="lds-loading">
            <div className="lds-skel lds-skel-hero" />
            <div className="lds-skel lds-skel-line" />
            <div className="lds-skel lds-skel-line" />
          </div>
        )}

        {err && !loading && (
          <div className="lds-err" role="alert">
            {err}
          </div>
        )}

        {!loading && cat && (
          <>
            <div className="lds-hero">
              {heroImgUrl ? (
                <img src={heroImgUrl} alt={cat.common_name} />
              ) : cat.has_video ? (
                <AutoVideo
                  src={`/api/livestock-catalog/${cat.id}/video`}
                  poster={
                    cat.image_url
                      ? `/api/livestock-image/${cat.id}`
                      : `/api/livestock-catalog/${cat.id}/video/poster`
                  }
                />
              ) : cat.image_url ? (
                // Same-origin cache proxy (see LivestockBrowser) so stock
                // artwork doesn't blank on a flaky third-party hotlink.
                <img
                  src={`/api/livestock-image/${cat.id}`}
                  alt={cat.common_name}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <div className="lds-hero-fallback">
                  {cat.kind === 'fish' ? '🐟' : cat.kind === 'coral' ? '🪸' : '🦐'}
                </div>
              )}
            </div>

            {/* Photo contribution. Hidden file input drives both the
                "add your photo" (owned) and "add to tank then photo"
                (not owned) flows. */}
            <input ref={fileRef} type="file" accept={MEDIA_ACCEPT} hidden onChange={onPickPhoto} />
            <div className="lds-photo-row">
              {userImages.length > 1 && (
                <div className="lds-photo-thumbs" aria-label="Your photos">
                  {userImages.slice(0, 5).map((img) => (
                    <img
                      key={img.id}
                      src={`/api/tanks/${tank.id}/livestock/${ownedResidentId}/image/${img.id}`}
                      alt=""
                      loading="lazy"
                    />
                  ))}
                </div>
              )}
              <button
                type="button"
                className="lds-photo-btn font-heading"
                disabled={photoBusy}
                onClick={() => (ownedResidentId ? fileRef.current?.click() : addThenAttach())}
              >
                {photoBusy
                  ? 'Working…'
                  : ownedResidentId
                    ? userImages.length
                      ? '+ Add another photo'
                      : '+ Add your photo'
                    : 'Add to tank and add a photo'}
              </button>
              <label className="lds-photo-share">
                <input
                  type="checkbox"
                  checked={sharePhoto}
                  onChange={(e) => setSharePhoto(e.target.checked)}
                  disabled={photoBusy}
                />
                <span>Share my photos with the catalog (an admin reviews them first)</span>
              </label>
              {photoMsg && <span className="lds-photo-msg">{photoMsg}</span>}
            </div>

            <header className="lds-header">
              <div className="lds-title-row">
                <h2 className="lds-name font-heading">{cat.common_name}</h2>
                {cat.category && <span className="lds-cat-chip">{cat.category}</span>}
              </div>
              <div className="lds-sci">
                <em>{cat.scientific_name}</em>
                {cat.family && <span> · {cat.family}</span>}
                <button
                  type="button"
                  className="lds-suggest-link"
                  onClick={() => setSuggestOpen((v) => !v)}
                >
                  Suggest an edit
                </button>
              </div>
            </header>

            {suggestOpen && (
              <SuggestEditForm
                catalog={cat}
                units={displayUnits}
                onDone={() => setSuggestOpen(false)}
              />
            )}

            <div className={`lds-verdict ${VERDICT_TONE[verdict]}`}>
              <div className="lds-verdict-head">
                <span className="lds-verdict-label">{VERDICT_LABEL[verdict] || verdict}</span>
                {typeof data.bioload_pct === 'number' && (
                  <span className="lds-bioload">~{data.bioload_pct}% bioload after add</span>
                )}
              </div>
              {fit?.reasons?.length > 0 && (
                <ul className="lds-reasons">
                  {fit.reasons.map((r, i) => (
                    <li key={i} className={`lds-reason lds-reason-${r.level}`}>
                      <span className="lds-reason-icon">{LEVEL_ICON[r.level] || '·'}</span>
                      <span>{r.message}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Quick facts — the at-a-glance row a hobbyist reads first. */}
            <div className="lds-specs">
              <SpecRow label="Type" value={cat.category} />
              <SpecRow label="Kind" value={cat.kind} />
              <SpecRow label="Care level" value={cat.care_level || raw.care_level} />
              <SpecRow
                label="Min tank"
                value={
                  cat.min_tank_gal
                    ? formatVolume(cat.min_tank_gal, displayUnits.volume, { decimals: 0 })
                    : null
                }
              />
              <SpecRow
                label="Rec tank"
                value={
                  cat.rec_tank_gal
                    ? formatVolume(cat.rec_tank_gal, displayUnits.volume, { decimals: 0 })
                    : null
                }
              />
              <SpecRow label="Temp range" value={tempRangeStr} />
              <SpecRow
                label="Max size"
                value={
                  cat.max_size_inches
                    ? displayUnits.volume === 'L'
                      ? `${Math.round(Number(cat.max_size_inches) * 2.54)} cm`
                      : `${cat.max_size_inches} in`
                    : null
                }
              />
              <SpecRow label="Reef safe" value={cat.reef_safe} />
              <SpecRow label="Temperament" value={raw.temperament} />
              <SpecRow label="Diet" value={raw.diet} />
              <SpecRow label="Min group" value={raw.min_group_size} />
              <SpecRow
                label="Captive-bred"
                value={cat.captive_bred === true ? 'yes' : cat.captive_bred === false ? 'no' : null}
              />
              {cat.iucn_status && (
                <SpecRow
                  label="Conservation"
                  value={`IUCN ${cat.iucn_status}`}
                  tone={cat.iucn_status === 'EN' || cat.iucn_status === 'CR' ? 'alert' : null}
                />
              )}
            </div>

            {/* Light & flow — the placement decision block. */}
            {hasLightFlow && (
              <div className="lds-section">
                <div className="lds-prose-eyebrow">LIGHT &amp; FLOW</div>
                <div className="lds-specs lds-specs-tight">
                  <SpecRow label="Light" value={care.light_pref || raw.lighting_preference} />
                  <SpecRow label="Flow" value={care.flow_pref || raw.flow_preference} />
                  <SpecRow label="Placement" value={care.placement} />
                  <SpecRow
                    label="Sensitivity"
                    value={care.sensitivity}
                    tone={care.sensitivity === 'high' ? 'alert' : null}
                  />
                </div>
              </div>
            )}

            {/* Target water — the vendor-style water-chemistry block. */}
            {hasWaterCard && (
              <div className="lds-water">
                <div className="lds-prose-eyebrow">TARGET WATER</div>
                <div className="lds-water-grid">
                  <WaterCell label="Alk" value={range(care.alk_dkh, ' dKH')} />
                  <WaterCell label="Calcium" value={range(care.calcium_ppm, ' ppm')} />
                  <WaterCell label="Magnesium" value={range(care.magnesium_ppm, ' ppm')} />
                  <WaterCell label="Nitrate" value={range(care.nitrate_ppm, ' ppm')} />
                  <WaterCell label="Phosphate" value={range(care.phosphate_ppm, ' ppm')} />
                  <WaterCell label="Temp" value={tempRangeStr} />
                </div>
              </div>
            )}

            <div className="lds-sensei">
              <div className="lds-sensei-head">
                <span className="lds-sensei-eyebrow">SENSEI'S TAKE</span>
                {advice?.from_cache && <span className="lds-sensei-cached">cached</span>}
                {adviceLoading && !advice && (
                  <span className="lds-sensei-thinking">Sensei is thinking…</span>
                )}
              </div>
              {advice ? (
                <p className="lds-sensei-text">{advice.advice}</p>
              ) : adviceLoading ? (
                <div className="lds-sensei-shimmer" aria-hidden="true">
                  <div className="lds-skel lds-skel-line" />
                  <div className="lds-skel lds-skel-line" style={{ width: '88%' }} />
                  <div className="lds-skel lds-skel-line" style={{ width: '64%' }} />
                </div>
              ) : tank.role === 'viewer' ? (
                <p className="lds-sensei-text lds-sensei-fallback">
                  Sensei&apos;s take shows here once the tank&apos;s owner or an editor has opened
                  this species. The rules verdict above is enough to act on.
                </p>
              ) : (
                <p className="lds-sensei-text lds-sensei-fallback">
                  Sensei is taking a short break. The rules verdict above is enough to act on.
                </p>
              )}
            </div>

            {raw.diet_details && (
              <div className="lds-prose">
                <div className="lds-prose-eyebrow">DIET</div>
                <p>{raw.diet_details}</p>
              </div>
            )}
            {raw.native_habitat && (
              <div className="lds-prose">
                <div className="lds-prose-eyebrow">NATIVE HABITAT</div>
                <p>{raw.native_habitat}</p>
              </div>
            )}

            {onAddedOnChange && (
              <label className="lds-added-on">
                <span className="font-heading">Date added to the tank</span>
                <input
                  type="date"
                  max={localDateIso()}
                  value={addedOn || ''}
                  onChange={(e) => onAddedOnChange(e.target.value)}
                />
              </label>
            )}
            <div className="lds-actions">
              <div className="lds-qty">
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={() => setQty((n) => Math.max(1, n - 1))}
                  disabled={qty <= 1}
                >
                  −
                </button>
                <span aria-live="polite">{qty}</span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  onClick={() => setQty((n) => Math.min(99, n + 1))}
                >
                  +
                </button>
              </div>
              <button
                type="button"
                className={`lds-add ${blockedAdd ? 'lds-add-blocked' : ''}`}
                onClick={handleAdd}
                disabled={adding}
              >
                {adding
                  ? 'Adding…'
                  : blockedAdd
                    ? 'Add anyway'
                    : `Add ${qty > 1 ? `${qty} ` : ''}to tank`}
              </button>
            </div>

            <div className="lds-credit">
              Image: Wikimedia Commons (CC BY-SA) or keeper-contributed. Species data: hand-curated
              saltwater catalog.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// User-submitted correction form. Inline, opened from the "Suggest an
// edit" link in the header. Posts to /api/livestock-catalog/:id/suggest-edit;
// admin reviews in catalog-suggestions.
const SUGGESTABLE = [
  { key: 'common_name', label: 'Common name' },
  { key: 'scientific_name', label: 'Scientific name' },
  { key: 'category', label: 'Type / category' },
  { key: 'family', label: 'Family' },
  { key: 'care_level', label: 'Care level' },
  { key: 'min_tank_gal', label: 'Min tank', unitKind: 'volume' },
  { key: 'rec_tank_gal', label: 'Recommended tank', unitKind: 'volume' },
  { key: 'max_size_inches', label: 'Max size (in)' },
  { key: 'temp_min_f', label: 'Temp min', unitKind: 'temp' },
  { key: 'temp_max_f', label: 'Temp max', unitKind: 'temp' },
  { key: 'reef_safe', label: 'Reef-safe (true/false)' },
  { key: 'care_params', label: 'Water targets (JSON)' },
  { key: 'notes', label: 'Notes' },
];
function SuggestEditForm({ catalog, units, onDone }) {
  const [field, setField] = useState('common_name');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  if (!catalog?.id) return null;
  const submit = async () => {
    setBusy(true);
    setMsg('');
    let suggested = value.trim();
    if (suggested === '') {
      setMsg('Enter the corrected value.');
      setBusy(false);
      return;
    }
    if (
      ['min_tank_gal', 'rec_tank_gal', 'max_size_inches', 'temp_min_f', 'temp_max_f'].includes(
        field
      )
    ) {
      const n = Number(suggested);
      if (!Number.isFinite(n)) {
        setMsg('Value must be a number.');
        setBusy(false);
        return;
      }
      const kind = SUGGESTABLE.find((o) => o.key === field)?.unitKind;
      suggested = kind ? catalogValueStored(n, kind, units) : n;
    } else if (field === 'reef_safe') {
      suggested = /^(true|yes|1)$/i.test(suggested);
    } else if (field === 'care_params') {
      try {
        suggested = JSON.parse(suggested);
      } catch {
        setMsg('Water targets must be valid JSON.');
        setBusy(false);
        return;
      }
    }
    try {
      await api(`/api/livestock-catalog/${catalog.id}/suggest-edit`, {
        method: 'POST',
        body: { field, suggested_value: suggested, note: note.trim() || null },
      });
      setMsg('Thanks — admin will review.');
      setValue('');
      setNote('');
      setTimeout(() => onDone?.(), 1500);
    } catch (e) {
      setMsg(e.message || 'Could not send.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lds-suggest">
      <div className="lds-prose-eyebrow">Suggest a correction</div>
      <label>
        Field
        <select value={field} onChange={(e) => setField(e.target.value)}>
          {SUGGESTABLE.map((o) => (
            <option key={o.key} value={o.key}>
              {catalogFieldLabel(o.label, o.unitKind, units)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Corrected value
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={field === 'care_params' ? '{"alk_dkh":[8,9.5]}' : ''}
        />
      </label>
      <label>
        Why? (optional)
        <textarea value={note} rows={2} maxLength={500} onChange={(e) => setNote(e.target.value)} />
      </label>
      {msg && <p className="lds-suggest-msg">{msg}</p>}
      <div className="lds-suggest-actions">
        <button type="button" onClick={() => onDone?.()} disabled={busy}>
          Cancel
        </button>
        <button type="button" className="lds-suggest-send" onClick={submit} disabled={busy}>
          {busy ? 'Sending…' : 'Send to admin'}
        </button>
      </div>
    </div>
  );
}

function SpecRow({ label, value, tone }) {
  if (value == null || value === '') return null;
  return (
    <div className="lds-spec">
      <span className="lds-spec-label">{label}</span>
      <span className={`lds-spec-value ${tone === 'alert' ? 'lds-spec-alert' : ''}`}>{value}</span>
    </div>
  );
}

function WaterCell({ label, value }) {
  if (!value) return null;
  return (
    <div className="lds-water-cell">
      <span className="lds-water-label font-heading">{label}</span>
      <span className="lds-water-value font-mono">{value}</span>
    </div>
  );
}
